import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute } from '@angular/router';
import { ElementRef, Optional } from '@angular/core';
import { contextIds, viewContextKey, viewContextVisible } from '../../view-context';
import { LocalStorageService } from '../../../services/local-storage.service';
import { CacheService } from '../../../services/cache.service';
import { DataSelectionBinding, DataViewSelection } from '../../../models/data-selection';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';
import {
  AfterContentInit, AfterViewChecked, AfterViewInit, booleanAttribute, Component,
  ContentChildren, EventEmitter, Input, isDevMode, OnChanges, OnDestroy,
  OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BrIconComponent } from '../icon/icon.component';
import { MatInputModule } from '@angular/material/input';
import { BrPopoverTriggerDirective } from '../popover/popover-trigger.directive';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DataFetch, DataQuery } from '../../../models/data-source';
import type { DataFilterValues } from '../data-filter/data-filter.component';
import { DataTableCellDirective } from './data-table-cell.directive';

export interface DataTableServerSort {
  field: string;
  desc: boolean;
}

/** Loads the children of one visible parent. Hierarchy is intentionally opt-in. */
export type DataTableChildrenFetch<T> = (parent: T, q: DataQuery) => Observable<T[]>;

/** One page-owned data column: its stable query id and user-facing label. */
export interface DataTableColumn {
  id: string;
  label: string;
  /** Defaults to the column id. Override only when the API uses another key. */
  sortKey?: string;
  /** Data columns sort by default. Set false only for a genuine exception. */
  sortable?: false;
  /** Right-align the column, header included. For quantities and dates, whose
   *  digits line up into a readable edge; prose stays left. */
  align?: 'end';
}

/** A server-backed management table. Business pages own its source and actions. */
@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatTableModule, MatSortModule, MatPaginatorModule,
    MatFormFieldModule, MatInputModule, BrIconComponent, MatButtonModule,
    MatTooltipModule, MatCheckboxModule, BrPopoverTriggerDirective, DataTableCellDirective,
    DragDropModule,
  ],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.css'],
  // One MatSort instance drives every generated data-column header.
  providers: [MatSort],
})
export class DataTableComponent<T = unknown>
  implements OnChanges, OnInit, AfterContentInit, AfterViewInit,
  AfterViewChecked, OnDestroy {
  /** Page-owned data columns, in display order. */
  @Input() contextKey = '';
  @Input() columns: readonly DataTableColumn[] = [];
  /** Server-side source for the current search, sort and page query. */
  @Input() fetch: DataFetch<T> | null = null;
  /** Applied business filters. A new value reloads server data from page 1. */
  @Input() filters: DataFilterValues = {};

  @Input({ transform: booleanAttribute }) searchable = true;
  @Input() searchPlaceholder = 'Search…';
  @Input() search = '';
  @Output() searchChange = new EventEmitter<string>();
  /** Shows an Add button in the trailing table header when set. */
  @Input() addLabel: string | null = null;
  /** Adds a standard per-row Edit action. The page owns the dialog and persistence. */
  @Input({ transform: booleanAttribute }) editable = false;
  @Input() editTooltip = 'Edit';
  /** Adds a leading checkbox column for multi-select; the page reads `selection.selected`. */
  @Input({ transform: booleanAttribute }) selectable = false;
  /** Per-row actions cell (a ⋮ menu, edit/delete icons, …). Given `let-row`, rendered
   *  in a trailing column for page-owned domain actions. */
  @Input() rowActions: TemplateRef<{ $implicit: T }> | null = null;
  /** Optional per-row detail, opened by a toggle beside the row actions and shown full width under the row. */
  @Input() rowDetail: TemplateRef<{ $implicit: T }> | null = null;

  /**
   * Whether a row can be dragged out of this table onto something else.
   *
   * Off everywhere but the page that asked for it. When on, the ROW is the
   * drag host but nothing starts a drag except an element the page marks with
   * `cdkDragHandle` inside its own `rowActions` - because a row that drags
   * from anywhere swallows the gesture that selects a cell's text, and being
   * able to copy an account number out of a table is not worth trading for a
   * shortcut. That is the same reason the row is not a button.
   *
   * Where a dragged row may LAND is the page's business: it owns the
   * cdkDropListGroup and the drop handler, and this component never learns
   * what the drag meant.
   */
  @Input() draggableRows = false;

  /** A row was picked up / put down. The page needs to know: on Territories
   *  the list of places to drop is folded away until one of these fires. */
  @Output() rowDragStarted = new EventEmitter<void>();
  @Output() rowDragEnded = new EventEmitter<void>();
  /** Optional server-side hierarchy. Root rows remain the unit of paging. */
  @Input() fetchChildren: DataTableChildrenFetch<T> | null = null;
  @Input() rowIdFn: (row: T) => unknown = row => this.rowProperty(row, 'id') ?? row;
  @Input() hasChildrenFn: (row: T) => boolean = row =>
    Number(this.rowProperty(row, 'childCount') ?? 0) > 0;
  /** localStorage key for the hidden-column set AND the column widths; empty =
   *  per-session only. */
  @Input() columnPickerKey = '';
  /**
   * Columns to start hidden, for a table whose full set does not fit its frame.
   *
   * A DEFAULT, not a restriction: every column stays in the picker, and the
   * first time this user shows or hides one their choice is stored and this
   * list is never consulted again. So it cannot fight a preference, and a
   * table that grows a column later does not re-hide what someone had chosen.
   *
   * Ignored without a `columnPickerKey`: there would be no way to reach the
   * hidden columns and no way to remember reaching them.
   */
  @Input() defaultHiddenColumns: readonly string[] = [];
  /** A key this table persisted under before it was renamed. Read once, then
   *  rewritten under the current key, so renaming a table does not silently
   *  reset every user's column widths and hidden set. */
  @Input() columnPickerLegacyKey = '';
  /** Excel-style column resizing: a grip on each header cell's right edge. */
  @Input({ transform: booleanAttribute }) resizableColumns = false;
  /**
   * Whether to show the pager. False is for a list embedded in a host page
   * that already bounds it - a run history for one date, say - where the page
   * frame belongs to the host and a pager under it is chrome for a set that
   * does not grow. The fetch still pages; set pageSize to cover the set.
   */
  @Input({ transform: booleanAttribute }) paginated = true;
  @Input() pageSize = 25;
  @Input() pageSizeOptions = [10, 25, 50];
  /** Optional deep-link state. Applied once before the first server request. */
  @Input() initialQuery: Partial<Pick<
    DataQuery,
    'sort' | 'desc' | 'page' | 'pageSize' | 'fields'
  >> | null = null;
  @Input() emptyText = 'Nothing here yet.';
  @Input() trackByFn: (index: number, row: T) => unknown = (index, row) =>
    this.rowProperty(row, 'id') ?? index;

  @Output() add = new EventEmitter<void>();
  @Output() edit = new EventEmitter<T>();
  /** The current page's selection. */
  @Output() selectionChange = new EventEmitter<T[]>();
  /** Fires before replacing the current page, including reloads. */
  @Output() queryChange = new EventEmitter<DataQuery>();
  @Output() loadError = new EventEmitter<unknown>();

  /** Unbound selection belongs to this page; a binding projects its owner. */
  readonly selection = new DataViewSelection<T>(
    () => this.loading ? [] : this.dataSource.data,
    row => this.rowIdFn(row),
  );
  @Input() set selectionBinding(binding: DataSelectionBinding | null) { this.selection.binding = binding; }
  get selectionBinding(): DataSelectionBinding | null { return this.selection.binding; }

  @ContentChildren(DataTableCellDirective) cellDefs!: QueryList<DataTableCellDirective<T>>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('scrollBox') scrollBox?: { nativeElement: HTMLElement };
  // The host-provided MatSort shared by every generated data header.
  constructor(
    readonly sort: MatSort,
    private cache: CacheService,
    private readonly storage: LocalStorageService,
    @Optional() private readonly context?: AppContextService,
    @Optional() private readonly contextRoute?: ActivatedRoute,
    @Optional() private readonly contextElement?: ElementRef<HTMLElement>,
  ) {
    cache.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      clearTimeout(this.contextTimer);
      this.restoredSelection = undefined;
      this.restoreScroll = null;
      this.fetchSub?.unsubscribe();
      this.resetHierarchy();
      this.openDetailIds.clear();
      this.setRootRows([]);
      this.total = 0;
      this.loading = false;
      this.errorMessage = '';
      this.dragColumn = null;
      this.dragGrip?.classList.remove('dt-col-grip--active');
      this.dragGrip = null;
      document.body.classList.remove('resizing-x');
      window.removeEventListener('pointermove', this.onColumnDragMove);
      window.removeEventListener('pointerup', this.onColumnDragEnd);
      this.hiddenColumns.clear();
      this.columnWidths.clear();
      this.restoreHidden();
      this.restoreWidths();
      this.applyWidthRules();
    });
  }

  readonly searchCtrl = new FormControl('', { nonNullable: true });
  dataSource = new MatTableDataSource<T>([]);
  loading = false;
  total = 0;
  errorMessage = '';

  private contextRegistration?: Subscription;
  private restoringSort = false;
  private contextTimer?: ReturnType<typeof setTimeout>;
  private selectionRevision = 0;
  private restoredSelection?: { version: number | undefined; selectionRevision: number; ids: (string | number)[]; scroll: number };
  private restoreScroll: number | null = null;

  private registerContext(): void {
    this.contextRegistration = this.context?.registerView({
      key: viewContextKey(this.contextRoute, `table:${this.contextKey || this.columnPickerKey || this.columns.map(column => column.id).join(',')}`),
      name: 'Visible table', priority: 10,
      active: () => viewContextVisible(this.contextElement),
      read: () => ({ query: this.currentQuery, loading: this.loading, error: this.errorMessage,
        total: this.total, selectionOwner: this.selectionBinding?.key ?? null,
        selectedIds: this.selectionBinding ? [] : contextIds(this.selection.selected.map(row => this.rowIdFn(row))),
        expandedIds: contextIds([...this.expandedRowIds]), detailIds: contextIds([...this.openDetailIds]),
        scroll: this.scrollBox?.nativeElement.scrollTop ?? 0 }),
      describe: state => JSON.stringify({ query: state.query, loading: state.loading,
        error: state.error, total: state.total, expandedIds: state.expandedIds, ...(state.selectionOwner ? { selectionOwner: state.selectionOwner }
          : { selectedIds: state.selectedIds.slice(0, 200), selectedCount: state.selectedIds.length }) }),
      restore: state => {
        const version = this.context?.restorationVersion;
        const selectionRevision = this.selectionRevision;
        clearTimeout(this.contextTimer);
        // Page-owned filters restore first; allow Angular to propagate inputs.
        this.contextTimer = setTimeout(() => {
          if (this.destroyed || version !== this.context?.restorationVersion) return;
          this.searchCtrl.setValue(state.query.search, { emitEvent: false });
          this.searchChange.emit(state.query.search);
          this.curSort = this.canSetServerSort(state.query.sort ?? '') ? state.query.sort ?? '' : '';
          this.curDesc = state.query.desc ?? false;
          this.sort.active = this.curSort;
          this.sort.direction = this.curSort ? (this.curDesc ? 'desc' : 'asc') : '';
          this.restoringSort = true;
          try { this.sort.sortChange.next({ active: this.sort.active, direction: this.sort.direction }); }
          finally { this.restoringSort = false; }
          this.page = state.query.page;
          this.pageSize = state.query.pageSize;
          if (this.paginator) {
            this.paginator.pageIndex = this.page - 1;
            this.paginator.pageSize = this.pageSize;
          }
          this.resetHierarchy();
          for (const id of state.expandedIds) this.expandedRowIds.add(id);
          // Row details open before leaving are open again on return.
          this.openDetailIds.clear();
          for (const id of state.detailIds ?? []) this.openDetailIds.add(id);
          this.restoredSelection = { version, selectionRevision, ids: state.selectedIds, scroll: state.scroll };
          this.load();
        });
      },
    });
  }

  private curSort = '';
  private curDesc = false;
  private page = 1;
  private readonly destroyed$ = new Subject<void>();
  private fetchSub?: Subscription;
  private rootRows: T[] = [];
  private viewReady = false;
  private destroyed = false;
  private currentQuery: DataQuery = {
    search: '', sort: '', desc: false, page: 1, pageSize: 25, fields: [], filters: {},
  };
  private readonly expandedRowIds = new Set<unknown>();
  private readonly openDetailIds = new Set<unknown>();
  private static nextCacheId = 0;
  private readonly cacheNamespace = `table-children:${++DataTableComponent.nextCacheId}`;
  private get childrenByParentId(): Map<unknown, T[]> {
    return this.cache.getOrCreate(this.cacheNamespace, 'rows', () => new Map<unknown, T[]>());
  }
  private readonly childParentIds = new Map<unknown, unknown>();
  private readonly loadingChildIds = new Set<unknown>();
  private readonly childFetchSubs = new Map<unknown, Subscription>();
  private readonly cellTemplates = new Map<string, TemplateRef<{ $implicit: T }>>();

  get serverSide(): boolean { return !!this.fetch; }
  get hierarchyEnabled(): boolean { return this.serverSide && !!this.fetchChildren; }
  /** Whether a server sort key belongs to one of this table's sortable
   * descriptors. Empty means the table's explicit unsorted state. */
  canSetServerSort(field: string): boolean {
    return field === '' || this.columns.some(column =>
      this.isSortable(column) && this.sortKeyFor(column) === field);
  }

  /** Snapshot the current server sort for an external Apply/Restore workflow. */
  getServerSort(): DataTableServerSort {
    return { field: this.curSort, desc: this.curDesc };
  }

  /** Set sort through the table boundary so header state, page, hierarchy and
   * the one server fetch move together. Pages must not mutate MatSort or the
   * component's private query fields directly. */
  setServerSort(field: string, desc: boolean): boolean {
    if (!this.serverSide || !this.canSetServerSort(field)) return false;
    this.sort.active = field;
    this.sort.direction = field ? (desc ? 'desc' : 'asc') : '';
    // MatSortHeader listens to sortChange to refresh its OnPush arrow/ARIA
    // state. The table's existing subscription below handles query state,
    // page reset, hierarchy invalidation and the single server fetch.
    this.sort.sortChange.next({ active: field, direction: this.sort.direction });
    return true;
  }

  get allColumns(): string[] {
    const cols: string[] = [];
    // The grip leads, because it is where the row is picked up from.
    if (this.draggableRows) cols.push('__drag');
    if (this.selectable) cols.push('__select');
    if (this.hierarchyEnabled) cols.push('__hierarchy');
    cols.push(...this.visibleColumns);
    if (this.actionsColumnEnabled) cols.push('__actions');
    return cols;
  }

  get actionsColumnEnabled(): boolean {
    return this.editable || this.rowActions != null || this.rowDetail != null || this.addLabel != null;
  }

  /** Every row gets a detail row only when the page gives a detail; it renders its content only while open. */
  readonly hasDetailRow = () => this.rowDetail != null;

  isDetailOpen(row: T): boolean { return this.openDetailIds.has(this.rowIdFn(row)); }

  toggleDetail(row: T): void {
    const id = this.rowIdFn(row);
    if (!this.openDetailIds.delete(id)) this.openDetailIds.add(id);
  }

  // ── Column hiding (Excel-style hide / unhide) ─────────────────────────────

  /** Hidden column ids. Only ever holds ids from `columns`, so a stale stored
   *  set from before a rename cannot blank a column that no longer exists. */
  readonly hiddenColumns = new Set<string>();
  private columnSelectionDirty = false;

  get columnPickerEnabled(): boolean { return this.columns.length > 0; }

  get tableSettingsEnabled(): boolean { return this.columnPickerEnabled || this.resizableColumns; }

  get visibleColumns(): string[] {
    return this.columns
      .map(column => column.id)
      .filter(id => !this.hiddenColumns.has(id));
  }

  cellTemplate(columnId: string): TemplateRef<{ $implicit: T }> | null {
    return this.cellTemplates.get(columnId) ?? null;
  }

  sortKeyFor(column: DataTableColumn): string {
    return column.sortKey ?? column.id;
  }

  isSortable(column: DataTableColumn): boolean {
    return column.sortable !== false;
  }

  cellValue(row: T, columnId: string): unknown {
    return (row as Record<string, unknown> | null)?.[columnId] ?? '—';
  }

  /** The last visible column cannot be hidden — an all-hidden table is a bug
   *  report, not a configuration. */
  canHide(id: string): boolean {
    return this.hiddenColumns.has(id)
      || this.visibleColumns.length > 1;
  }

  toggleColumn(id: string): void {
    const wasHidden = this.hiddenColumns.has(id);
    if (wasHidden) this.hiddenColumns.delete(id);
    else if (this.canHide(id)) this.hiddenColumns.add(id);
    else return;
    this.persistHidden();
    this.columnSelectionDirty = true;
  }

  /** The Excel "unhide": every column back in one move. */
  showAllColumns(): void {
    if (!this.hiddenColumns.size) return;
    this.hiddenColumns.clear();
    this.persistHidden();
    this.columnSelectionDirty = true;
  }

  /** Apply a batch of checkbox changes once the picker closes. */
  applyColumnSelection(): void {
    if (!this.columnSelectionDirty) return;
    this.columnSelectionDirty = false;
    this.resetHierarchy();
    this.load();
  }

  // ── Column widths (Excel-style drag on the header's right edge) ───────────
  //
  // Widths are applied through a stylesheet keyed on Material's per-column
  // `mat-column-{id}` class, not by touching cells: rows are virtualised in and
  // out on every page, and a rule covers the ones that do not exist yet.
  //
  // The rules carry !important so a user-resized width overrides the column's
  // descriptor layout on every rendered row.

  private readonly columnWidths = new Map<string, number>();
  /** Scopes the generated rules to this table instance. */
  readonly instanceId = `dt${++DataTableComponent.instances}`;
  private static instances = 0;
  private styleEl?: HTMLStyleElement;
  private gripSignature = '';
  private dragColumn: string | null = null;
  private dragGrip: HTMLElement | null = null;
  private dragStartX = 0;
  private dragStartWidth = 0;

  get hasCustomWidths(): boolean { return this.columnWidths.size > 0; }

  /** Put every column back to the width its page asked for. */
  resetColumnWidths(): void {
    this.columnWidths.clear();
    this.applyWidthRules();
    this.persistWidths();
  }

  private applyWidthRules(): void {
    if (!this.styleEl) {
      this.styleEl = document.createElement('style');
      document.head.appendChild(this.styleEl);
    }
    const rules = [...this.columnWidths].map(([id, px]) =>
      `[data-dt-id="${this.instanceId}"] .mat-column-${id}{` +
      `flex:0 0 ${px}px!important;min-width:${px}px!important;max-width:${px}px!important;}`);
    this.styleEl.textContent = rules.join('\n');
  }

  /**
   * Give every header cell a grip, and take them off cells that lost one.
   * Driven from ngAfterViewChecked because the header is rebuilt whenever the
   * column set changes (hide / unhide), and there is no output for that — the
   * signature guard keeps the common no-op to a string compare.
   */
  private syncGrips(): void {
    if (!this.resizableColumns) return;
    const host = this.scrollBox?.nativeElement;
    if (!host) return;
    const signature = this.allColumns.join('|');
    if (signature === this.gripSignature) return;
    this.gripSignature = signature;

    host.querySelectorAll<HTMLElement>('.mat-mdc-header-cell').forEach(cell => {
      // The checkbox and actions columns are furniture, not data: nothing to
      // widen, and a grip there would just be a dead target.
      // Array.from, not spread: the TS lib target here does not give
      // DOMTokenList an iterator.
      const id = Array.from(cell.classList)
        .find(c => c.startsWith('mat-column-'))?.slice('mat-column-'.length);
      if (!id || id.startsWith('__') || cell.querySelector('.dt-col-grip')) return;
      const grip = document.createElement('div');
      grip.className = 'dt-col-grip';
      grip.title = 'Drag to resize · double-click to reset';
      grip.addEventListener(
        'pointerdown', e => this.beginColumnDrag(e, id, cell, grip));
      // Double-click resets ONE column, the way Excel's border double-click
      // auto-fits one. Stopped so it never reaches the sort header underneath.
      grip.addEventListener('dblclick', e => {
        e.preventDefault(); e.stopPropagation();
        this.columnWidths.delete(id);
        this.applyWidthRules();
        this.persistWidths();
      });
      cell.appendChild(grip);
    });
  }

  private beginColumnDrag(
    e: PointerEvent, id: string, cell: HTMLElement, grip: HTMLElement): void {
    e.preventDefault();
    // The grip sits on a mat-sort-header; without this, every resize also sorts.
    e.stopPropagation();
    this.dragColumn = id;
    this.dragStartX = e.clientX;
    this.dragStartWidth = cell.getBoundingClientRect().width;
    // Marked on the one grip being dragged, not on <body>: the highlight is how
    // the header says WHICH boundary is moving, and a document-level class says
    // it about all of them at once.
    this.dragGrip = grip;
    grip.classList.add('dt-col-grip--active');
    document.body.classList.add('resizing-x');
    window.addEventListener('pointermove', this.onColumnDragMove);
    window.addEventListener('pointerup', this.onColumnDragEnd);
  }

  private readonly onColumnDragMove = (e: PointerEvent) => {
    if (!this.dragColumn) return;
    const width = Math.round(
      Math.max(60, Math.min(900, this.dragStartWidth + (e.clientX - this.dragStartX))));
    this.columnWidths.set(this.dragColumn, width);
    this.applyWidthRules();
  };

  private readonly onColumnDragEnd = (e: PointerEvent) => {
    this.onColumnDragMove(e);
    this.dragColumn = null;
    this.dragGrip?.classList.remove('dt-col-grip--active');
    this.dragGrip = null;
    document.body.classList.remove('resizing-x');
    window.removeEventListener('pointermove', this.onColumnDragMove);
    window.removeEventListener('pointerup', this.onColumnDragEnd);
    this.persistWidths();
  };

  private persistWidths(): void {
    if (!this.columnPickerKey) return;
    this.storage.set(`dt-widths:${this.columnPickerKey}`, [...this.columnWidths]);
  }

  /**
   * Reads one persisted slice, falling back to the table's former key and
   * moving what it finds over to the current one. The move happens on the
   * first read after a rename and never again; a legacy entry is only
   * consulted when nothing is stored under the current key, so a user who has
   * already saved a layout post-rename is not dragged backwards.
   */
  private readPersisted(prefix: string): string | null {
    const current = this.storage.getItem(`${prefix}:${this.columnPickerKey}`);
    if (current !== null || !this.columnPickerLegacyKey) return current;

    const legacy = this.storage.getItem(`${prefix}:${this.columnPickerLegacyKey}`);
    if (legacy === null) return null;
    if (this.storage.setItem(`${prefix}:${this.columnPickerKey}`, legacy)) {
      this.storage.removeItem(`${prefix}:${this.columnPickerLegacyKey}`);
    }
    return legacy;
  }

  private restoreWidths(): void {
    if (!this.columnPickerKey) return;
    try {
      const raw = this.readPersisted('dt-widths');
      if (!raw) return;
      for (const [id, px] of JSON.parse(raw) as [string, number][]) {
        // Same guard as the hidden set: an id that is no longer a column is
        // dropped rather than pinning a width onto nothing.
        if (this.columns.some(column => column.id === id) && Number.isFinite(px))
          this.columnWidths.set(id, px);
      }
      this.applyWidthRules();
    } catch { /* unreadable state loses the widths, nothing else */ }
  }

  private persistHidden(): void {
    if (!this.columnPickerKey) return;
    this.storage.set(`dt-hidden:${this.columnPickerKey}`, [...this.hiddenColumns]);
  }

  private restoreHidden(): void {
    if (!this.columnPickerKey) return;
    try {
      const raw = this.readPersisted('dt-hidden');
      // Nothing stored is a first visit, not an empty choice: the page's
      // opening set applies. Unreadable state falls here too, and showing
      // every column would be the wrong way to fail on a narrow frame.
      if (!raw) { this.applyDefaultHidden(); return; }
      for (const id of JSON.parse(raw) as string[]) {
        if (this.columns.some(column => column.id === id)) this.hiddenColumns.add(id);
      }
    } catch { this.applyDefaultHidden(); }
  }

  private applyDefaultHidden(): void {
    for (const id of this.defaultHiddenColumns) {
      // Never the last one standing, for the reason canHide gives.
      if (this.columns.some(column => column.id === id) && this.canHide(id))
        this.hiddenColumns.add(id);
    }
  }

  /** True when every row on the current page is selected. */
  isAllSelected(): boolean {
    const data = this.currentPageRows();
    return data.length > 0 && data.every(r => this.selection.isSelected(r));
  }

  /** Header checkbox: select or clear the whole current page. */
  toggleAll(): void {
    if (this.loading) return;
    this.selectionRevision++;
    if (this.isAllSelected()) this.selection.clear();
    else this.selection.select(...this.currentPageRows());
  }

  toggleRow(row: T): void {
    if (this.loading) return;
    this.selectionRevision++;
    this.selection.toggle(row);
  }

  clearSelection(): void {
    this.selectionRevision++;
    this.selection.clear();
  }

  isChildRow(row: T): boolean { return this.childParentIds.has(this.rowIdFn(row)); }

  isExpanded(row: T): boolean { return this.expandedRowIds.has(this.rowIdFn(row)); }

  isChildLoading(row: T): boolean { return this.loadingChildIds.has(this.rowIdFn(row)); }

  canExpand(row: T): boolean {
    return this.hierarchyEnabled && !this.isChildRow(row) && this.hasChildrenFn(row);
  }

  toggleChildren(row: T): void {
    if (!this.canExpand(row)) return;
    const id = this.rowIdFn(row);
    if (this.expandedRowIds.has(id)) {
      this.expandedRowIds.delete(id);
      // A row nobody can see must not still be in the selection. The page acts
      // on `selection.selected`, so a child left ticked behind a collapsed
      // parent is a row that gets deleted, exported or reassigned without
      // being on screen when the button was pressed.
      this.deselectChildrenOf(id);
      this.rebuildDisplayedRows();
      return;
    }
    this.expandedRowIds.add(id);
    if (this.childrenByParentId.has(id)) this.rebuildDisplayedRows();
    else this.loadChildren(row);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const filterChange = changes['filters'];
    const searchChanged = !!changes['search'] && this.searchCtrl.value !== this.search;
    if (searchChanged) this.searchCtrl.setValue(this.search, { emitEvent: false });
    // The initial view load already reads the latest filters. Later immutable
    // filter objects represent an explicit apply and start a fresh query.
    if (!this.viewReady || (!searchChanged && (!filterChange || filterChange.firstChange))) return;
    this.resetToFirstPage();
    this.resetHierarchy();
    this.load();
  }

  ngOnInit(): void {
    this.searchCtrl.setValue(this.search, { emitEvent: false });
    this.sort.disableClear = true;
    this.restoreHidden();
    this.restoreWidths();
    this.applyInitialQuery();
    this.registerContext();
    this.selection.changed.pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.selectionChange.emit([...this.selection.selected]));

    this.searchCtrl.valueChanges.pipe(
      debounceTime(300), distinctUntilChanged(), takeUntil(this.destroyed$),
    ).subscribe(value => {
      if (value !== this.searchCtrl.value) return;
      this.searchChange.emit(value);
      this.resetToFirstPage();
      this.resetHierarchy();
      this.load();
    });
  }

  // The table owns headers and column registration. Pages project only body
  // templates, keyed by the same id as the column descriptor.
  ngAfterContentInit(): void {
    const register = () => {
      this.cellTemplates.clear();
      this.cellDefs.forEach(cell => this.cellTemplates.set(cell.columnId, cell.template));
      this.reportOrphanCellTemplates();
    };
    register();
    this.cellDefs.changes.pipe(takeUntil(this.destroyed$)).subscribe(register);
  }

  /**
   * A cell template keyed to no column is a wiring mistake that hides.
   *
   * The template renders nowhere, and the column it was meant for falls back
   * to `row[id]` — usually undefined, so the cell prints "—" and reads as
   * missing DATA rather than missing WIRING. That is exactly how a rename
   * slipped through on the Leads table: the descriptor became `salesRep`
   * while the template stayed `dtCell="owner"`, and the Sales rep column
   * showed a dash on every row until somebody noticed the export was wrong.
   *
   * Dev-mode only, and loud: there is no correct program in which a page
   * projects a template for a column its table does not have.
   */
  private reportOrphanCellTemplates(): void {
    if (!isDevMode()) return;
    const known = new Set(this.columns.map(column => column.id));
    const orphans = [...this.cellTemplates.keys()].filter(id => !known.has(id));
    if (orphans.length === 0) return;
    console.error(
      `[app-data-table] cell template for unknown column(s): ${orphans.join(', ')}.`
      + ` The table's columns are: ${[...known].join(', ')}.`);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.initialQuery?.sort) {
      this.sort.active = this.initialQuery.sort;
      this.sort.direction = this.initialQuery.desc ? 'desc' : 'asc';
    }
    if (this.paginator) this.paginator.pageIndex = this.page - 1;
    this.sort.sortChange.pipe(takeUntil(this.destroyed$)).subscribe((s: Sort) => {
      if (this.restoringSort) return;
      this.curSort = s.direction ? s.active : '';
      this.curDesc = s.direction === 'desc';
      this.resetToFirstPage();
      this.resetHierarchy();
      this.load();
    });
    // Absent when paginated is false: the control is not rendered, so there
    // are no page events to listen for. The fetch still pages.
    this.paginator?.page.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.page = this.paginator.pageIndex + 1;
      this.resetHierarchy();
      this.load();
    });
    // First load in a microtask: synchronous here, `loading` flips inside the
    // same change-detection pass that created this view and dev mode throws
    // NG0100. Every later load starts from an event, which has no such pass.
    Promise.resolve().then(() => this.load());
  }

  private rowProperty(row: T, key: string): unknown {
    return typeof row === 'object' && row !== null
      ? (row as Record<string, unknown>)[key]
      : undefined;
  }

  private applyInitialQuery(): void {
    const initial = this.initialQuery;
    if (!initial) return;
    this.curSort = initial.sort ?? '';
    this.curDesc = initial.desc ?? false;
    this.page = Math.max(1, initial.page ?? 1);
    this.pageSize = Math.max(1, initial.pageSize ?? this.pageSize);
    if (!initial.fields?.length) return;
    const visible = new Set(initial.fields);
    const valid = this.columns.map(column => column.id).filter(id => visible.has(id));
    if (!valid.length) return;
    this.hiddenColumns.clear();
    this.columns.forEach(column => {
      if (!visible.has(column.id)) this.hiddenColumns.add(column.id);
    });
  }

  ngAfterViewChecked(): void {
    if (this.restoreScroll !== null && this.scrollBox && !this.loading) {
      this.scrollBox.nativeElement.scrollTop = this.restoreScroll;
      this.restoreScroll = null;
    }
    this.syncGrips();
  }

  ngOnDestroy(): void {
    this.contextRegistration?.unsubscribe();
    clearTimeout(this.contextTimer);
    this.selection.destroy();
    this.destroyed = true;
    window.removeEventListener('pointermove', this.onColumnDragMove);
    window.removeEventListener('pointerup', this.onColumnDragEnd);
    // `resizing-x` lives on <body>, so a table torn down mid-drag would leave
    // the whole page stuck with a col-resize cursor and no text selection.
    document.body.classList.remove('resizing-x');
    this.styleEl?.remove();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.fetchSub?.unsubscribe();
    this.childFetchSubs.forEach(s => s.unsubscribe());
    this.cache.clear(this.cacheNamespace);
  }

  /**
   * Re-fetches the current page after a write. Supplying the changed row(s)
   * invalidates only their hierarchy branches; omit them when the affected
   * branch is unknown and every expanded child cache must be refreshed.
   */
  refresh(changedRows?: readonly T[]): void {
    if (!this.serverSide) return;
    if (changedRows === undefined) {
      this.invalidateChildren();
    } else {
      const parentIds = new Set(changedRows.map(row => {
        const rowId = this.rowIdFn(row);
        return this.childParentIds.get(rowId) ?? rowId;
      }));
      parentIds.forEach(id => this.invalidateChildrenOf(id));
      this.rebuildDisplayedRows();
    }
    this.load();
  }

  /** Re-fetch from page 1 in server mode (e.g. after an external filter change). */
  reload(): void {
    if (!this.serverSide) return;
    this.resetToFirstPage();
    this.resetHierarchy();
    this.load();
  }

  retry(): void { this.load(); }

  private load(): void {
    if (!this.fetch || this.destroyed) return;
    const restoration = this.restoredSelection;
    this.restoredSelection = undefined;
    const q: DataQuery = {
      search: (this.searchCtrl.value ?? '').trim(),
      sort: this.curSort,
      desc: this.curDesc,
      page: this.page,
      pageSize: this.paginator?.pageSize ?? this.pageSize,
      fields: this.visibleColumns,
      filters: { ...this.filters },
    };
    this.currentQuery = q;
    this.queryChange.emit(q);
    // A query change invalidates the meaning of the current selection immediately.
    // Do not leave destructive bulk actions armed while the replacement page is
    // loading, or indefinitely when that request fails.
    this.loading = true;
    this.selection.resetLocal();
    this.selection.rowsChanged();
    this.errorMessage = '';
    this.fetchSub?.unsubscribe();
    this.fetchSub = this.fetch(q).pipe(takeUntil(this.destroyed$)).subscribe({
      next: p => {
        const lastPage = Math.max(1, Math.ceil(p.total / q.pageSize));
        if (q.page > lastPage) {
          // A write can remove the last row on the last page. Move to the final
          // valid page instead of leaving the user on an empty, out-of-range page.
          this.page = lastPage;
          if (this.paginator) this.paginator.pageIndex = lastPage - 1;
          this.load();
          return;
        }
        this.loading = false;
        this.page = q.page;
        this.total = p.total;
        this.setRootRows(p.items);
        if (restoration && restoration.version === this.context?.restorationVersion) {
          const held = restoration;
          if (held.selectionRevision === this.selectionRevision && this.selectable && !this.selectionBinding) this.selection.select(...p.items.filter(row => held.ids.includes(this.rowIdFn(row) as string | number)));
          this.restoreScroll = held.scroll;
        }
        if (this.hierarchyEnabled) {
          p.items.forEach(row => {
            const id = this.rowIdFn(row);
            if (this.expandedRowIds.has(id) && !this.childrenByParentId.has(id))
              this.loadChildren(row, restoration);
          });
        }
      },
      error: err => {
        this.loading = false;
        this.total = 0;
        this.setRootRows([]);
        this.errorMessage = 'Unable to load this view. Try again.';
        this.loadError.emit(err);
      },
    });
  }

  private resetToFirstPage(): void {
    this.page = 1;
    if (this.paginator) this.paginator.pageIndex = 0;
  }

  private currentPageRows(): T[] {
    return this.dataSource.data;
  }

  private setRootRows(rows: T[], clearSelection = true): void {
    this.rootRows = rows;
    if (clearSelection) this.selection.resetLocal();
    this.rebuildDisplayedRows();
  }


  /** Unticks the children of a parent that is no longer showing them. */
  private deselectChildrenOf(parentId: unknown): void {
    if (this.selectionBinding) return;
    for (const child of this.childrenByParentId.get(parentId) ?? []) {
      this.selection.deselect(child);
    }
  }

  private rebuildDisplayedRows(): void {
    this.childParentIds.clear();
    const displayed: T[] = [];
    for (const root of this.rootRows) {
      displayed.push(root);
      const parentId = this.rowIdFn(root);
      if (!this.expandedRowIds.has(parentId)) continue;
      for (const child of this.childrenByParentId.get(parentId) ?? []) {
        this.childParentIds.set(this.rowIdFn(child), parentId);
        displayed.push(child);
      }
    }
    this.dataSource.data = displayed;
    this.selection.rowsChanged();
  }

  private loadChildren(parent: T, restoration?: { version: number | undefined; selectionRevision: number; ids: (string | number)[] }): void {
    if (!this.fetchChildren) return;
    const id = this.rowIdFn(parent);
    this.childFetchSubs.get(id)?.unsubscribe();
    this.loadingChildIds.add(id);
    const sub = this.cache.track(() => this.fetchChildren!(parent, this.currentQuery)).pipe(takeUntil(this.destroyed$)).subscribe({
      next: children => {
        this.loadingChildIds.delete(id);
        this.childrenByParentId.set(id, children ?? []);
        this.rebuildDisplayedRows();
        if (restoration?.version === this.context?.restorationVersion && restoration
            && restoration.selectionRevision === this.selectionRevision
            && this.selectable && !this.selectionBinding && this.expandedRowIds.has(id)) {
          this.selection.select(...(children ?? []).filter(child => restoration.ids.includes(this.rowIdFn(child) as string | number)));
        }
      },
      error: err => {
        this.loadingChildIds.delete(id);
        this.expandedRowIds.delete(id);
        this.deselectChildrenOf(id);
        this.loadError.emit(err);
        this.rebuildDisplayedRows();
      },
    });
    this.childFetchSubs.set(id, sub);
  }

  private resetHierarchy(): void {
    this.invalidateChildren();
    this.expandedRowIds.clear();
  }

  private invalidateChildrenOf(parentId: unknown): void {
    this.childFetchSubs.get(parentId)?.unsubscribe();
    this.childFetchSubs.delete(parentId);
    this.childrenByParentId.delete(parentId);
    this.loadingChildIds.delete(parentId);
  }

  /** Drops in-flight/cached children while retaining which roots are open. */
  private invalidateChildren(): void {
    this.childFetchSubs.forEach(s => s.unsubscribe());
    this.childFetchSubs.clear();
    this.childrenByParentId.clear();
    this.childParentIds.clear();
    this.loadingChildIds.clear();
    this.rebuildDisplayedRows();
  }
}
